// Fixtures for the Logic Decomposer dashboard.
//
// Transcribed from the ARTIFACTS array in "Logic Decomposer Dashboard v3.dc.html" so the
// module renders the mockup's own data, field-for-field, before the §4 endpoints exist.
// The eight rows are a sample of the 247-artifact portfolio; counts_by_disposition carries
// the mockup's full-portfolio numbers, which is why they do not sum to eight.
//
// Every field maps to a type in ./types.ts. When the endpoints land, these become the
// fallback the module renders when a fetch fails — not a second source of truth.

import type { ArtifactDetail, FrameworkSummary, PhaseDef } from './types';

export const artifactDetails: Record<string, ArtifactDetail> = {
  "r1": {
    "id": "r1",
    "name": "Y9C_Schedule_HC-C_Consolidation.xlsm",
    "owner": "CFO · Regulatory Reporting",
    "assignee": "J. Reyes",
    "timing_cat": 1,
    "outcome": "box1",
    "disposition": "Migrate",
    "target_state_label": "Full SDLC rebuild",
    "confidence": 0.91,
    "rationale": "High regulatory exposure (0.94) and bespoke consolidation logic that no enterprise asset absorbs. Complexity + criticality justify Full SDLC rebuild over lift-&-shift.",
    "purpose": "Rolls up trial-balance journal entries by legal entity into the Y-9C HC-C consolidation schedule with FX translation and materiality classification.",
    "last_modified": "3 days ago",
    "usage_summary": "Monthly · 12 runs",
    "sources": [
      "Oracle GL",
      "FX Rates (Bloomberg)",
      "Entity master"
    ],
    "targets": [
      "Y-9C HC-C Consolidation"
    ],
    "transforms": [
      {
        "op": "JOIN",
        "desc": "gl_journal ⨝ fx_rates on ccy, as_of"
      },
      {
        "op": "FILTER",
        "desc": "entity = ACME-NY"
      },
      {
        "op": "DERIVE",
        "desc": "materiality classifier · Tier1/Tier2/Well-cap"
      },
      {
        "op": "AGGREGATE",
        "desc": "SUM(amount_usd) BY schedule_line"
      }
    ],
    "transform_count": 14,
    "counts": {
      "formulas": 412,
      "macros": 27,
      "hardcodes": 3,
      "lookups": 4,
      "total_statements": 442,
      "distinct_rules": 4
    },
    "logic_rows": [
      {
        "tag": "HC",
        "location": "Sheet2!F14 · Materiality threshold",
        "code_html": "IF(D14&gt;<b style=\"color:#ff6b8a\">2500000</b>, \"Material\", \"Immaterial\")"
      },
      {
        "tag": "HC",
        "location": "Module1.bas:42 · Tier-1 classifier",
        "code_html": "If ratio &lt; <b style=\"color:#ff6b8a\">0.08</b> Then category = \"T1\"<br>ElseIf ratio &lt; <b style=\"color:#ff6b8a\">0.105</b> Then category = \"T2\"<br>Else category = \"Well-cap\""
      },
      {
        "tag": "HC",
        "location": "Sheet2!K7 · Entity filter",
        "code_html": "SUMIF(entity, \"<b style=\"color:#ff6b8a\">ACME-NY</b>\", amount)"
      },
      {
        "tag": "FN",
        "location": "Module2.bas:118 · FX conversion",
        "code_html": "Function ConvertToUSD(amt, ccy, dt)<br>&nbsp;&nbsp;rate = VLOOKUP(ccy&amp;dt, fx_rates, 3, FALSE)<br>&nbsp;&nbsp;ConvertToUSD = amt * rate"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.86 · High",
        "level": "high",
        "tooltip": "Blast radius — downstream fan-in from cross-run entity rollup (scoreEntity). High = changes here ripple widely."
      },
      {
        "label": "Reg exposure",
        "value": "0.94",
        "level": "high",
        "tooltip": "Regulatory exposure — probability this artifact feeds a regulated filing. Any non-zero routes to human review."
      },
      {
        "label": "Platform replace",
        "value": "0.28",
        "level": "medium",
        "tooltip": "Absorbability into a governed enterprise asset. Low = bespoke logic that can't be lifted-and-shifted."
      },
      {
        "label": "Overlap",
        "value": "0.04",
        "level": "low",
        "tooltip": "Semantic similarity with other artifacts. High = duplicate candidate."
      }
    ],
    "overlaps": []
  },
  "r2": {
    "id": "r2",
    "name": "Loan_Loss_Provisioning.yxmd",
    "owner": "CFO · Credit risk",
    "assignee": "M. Tan",
    "timing_cat": 1,
    "outcome": "box3",
    "disposition": "Migrate",
    "target_state_label": "Platform migration",
    "confidence": 0.96,
    "rationale": "High enterprise-asset match (0.87) — Genius Bank source, Manubank target already governed. Straight platform migration; no bespoke rebuild needed.",
    "purpose": "CECL-aligned loan-loss provisioning across the commercial book. Rolls into Manubank's governed credit-risk pipeline.",
    "last_modified": "1 day ago",
    "usage_summary": "Weekly · 48 runs",
    "sources": [
      "Genius Bank · Loan Master",
      "Rate table"
    ],
    "targets": [
      "Manubank · Provision Register"
    ],
    "transforms": [
      {
        "op": "JOIN",
        "desc": "loans ⨝ rates"
      },
      {
        "op": "FORMULA",
        "desc": "expected credit loss"
      },
      {
        "op": "AGGREGATE",
        "desc": "SUM BY portfolio_segment"
      }
    ],
    "transform_count": 7,
    "counts": {
      "formulas": 0,
      "macros": 0,
      "hardcodes": 2,
      "lookups": 3,
      "total_statements": 118,
      "distinct_rules": 3
    },
    "logic_rows": [
      {
        "tag": "HC",
        "location": "Formula tool #14 · Loss rate floor",
        "code_html": "IF(loss_rate &lt; <b style=\"color:#ff6b8a\">0.005</b>, 0.005, loss_rate)"
      },
      {
        "tag": "FN",
        "location": "JOIN Multiple #3 · Rate lookup",
        "code_html": "JOIN loans ⨝ rate_table ON segment, vintage"
      },
      {
        "tag": "HC",
        "location": "Filter tool #7 · Portfolio scope",
        "code_html": "segment IN (\"<b style=\"color:#ff6b8a\">C&I</b>\", \"<b style=\"color:#ff6b8a\">CRE</b>\")"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.71 · High",
        "level": "high",
        "tooltip": "Feeds 6 downstream reports across CFO & CRO."
      },
      {
        "label": "Reg exposure",
        "value": "0.62",
        "level": "medium",
        "tooltip": "CECL-tagged; SOX-relevant."
      },
      {
        "label": "Platform replace",
        "value": "0.87",
        "level": "low",
        "tooltip": "Manubank pipeline already governs this workflow class."
      },
      {
        "label": "Overlap",
        "value": "0.11",
        "level": "low",
        "tooltip": "Small overlap with Q2 provisioning batch."
      }
    ],
    "overlaps": [
      {
        "name": "Loan_Provision_Q2_batch.yxmd",
        "similarity": 0.11
      }
    ]
  },
  "r3": {
    "id": "r3",
    "name": "FX_Snapshot_Legacy_2019.xlsm",
    "owner": "Treasury · FX",
    "assignee": "unassigned",
    "timing_cat": 3,
    "outcome": "decommission",
    "disposition": "Decommission",
    "target_state_label": "Sunset",
    "confidence": 0.94,
    "rationale": "92% semantic overlap with the current FX_Snapshot.yxmd. Zero usage in 340 days. Sunset with redirect.",
    "purpose": "Legacy daily FX snapshot, superseded by the Alteryx-based FX_Snapshot workflow.",
    "last_modified": "340 days ago",
    "usage_summary": "Idle",
    "sources": [
      "Bloomberg FX"
    ],
    "targets": [
      "FX_Daily_Report.xlsx"
    ],
    "transforms": [
      {
        "op": "FILTER",
        "desc": "currencies IN majors"
      },
      {
        "op": "AGGREGATE",
        "desc": "daily close"
      }
    ],
    "transform_count": 4,
    "counts": {
      "formulas": 88,
      "macros": 2,
      "hardcodes": 0,
      "lookups": 1,
      "total_statements": 112,
      "distinct_rules": 1
    },
    "logic_rows": [
      {
        "tag": "FN",
        "location": "Sheet1!B2:B33 · Daily aggregation",
        "code_html": "AVERAGE(fx_rate_snapshot[currency])"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.09 · Low",
        "level": "low",
        "tooltip": "No active consumers."
      },
      {
        "label": "Reg exposure",
        "value": "0.00",
        "level": "low",
        "tooltip": "No regulatory feed."
      },
      {
        "label": "Platform replace",
        "value": "0.92",
        "level": "low",
        "tooltip": "Replaced by FX_Snapshot.yxmd."
      },
      {
        "label": "Overlap",
        "value": "0.92",
        "level": "high",
        "tooltip": "Near-identical to FX_Snapshot.yxmd."
      }
    ],
    "overlaps": [
      {
        "name": "FX_Snapshot.yxmd",
        "similarity": 0.92
      }
    ]
  },
  "r4": {
    "id": "r4",
    "name": "Basel_III_Recon_Q3.xlsm",
    "owner": "CRO · Basel",
    "assignee": "unresolved",
    "timing_cat": 3,
    "outcome": "queue",
    "disposition": "Queue",
    "target_state_label": "Priority 0.81 · defer W3",
    "confidence": 0.81,
    "rationale": "Cannot responsibly disposition until ownership is confirmed. Deferred to Wave 3 with priority 0.81 (criticality × blast-radius).",
    "purpose": "Quarterly Basel III capital reconciliation. Owner attribution failed against Apparity — pending resolution.",
    "last_modified": "8 days ago",
    "usage_summary": "Quarterly · 4 runs",
    "sources": [
      "GL · CRO extract",
      "RWA feed"
    ],
    "targets": [
      "Basel III Recon Report"
    ],
    "transforms": [
      {
        "op": "JOIN",
        "desc": "gl ⨝ rwa"
      },
      {
        "op": "AGGREGATE",
        "desc": "capital tiers"
      }
    ],
    "transform_count": 11,
    "counts": {
      "formulas": 287,
      "macros": 12,
      "hardcodes": 5,
      "lookups": 6,
      "total_statements": 340,
      "distinct_rules": 1
    },
    "logic_rows": [
      {
        "tag": "HC",
        "location": "Sheet3!G22 · Tier-1 minimum",
        "code_html": "IF(t1_ratio &lt; <b style=\"color:#ff6b8a\">0.06</b>, \"Breach\", \"OK\")"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.68 · Medium",
        "level": "medium",
        "tooltip": "Feeds Basel III filing."
      },
      {
        "label": "Reg exposure",
        "value": "0.88",
        "level": "high",
        "tooltip": "Basel-tagged."
      },
      {
        "label": "Platform replace",
        "value": "0.31",
        "level": "medium",
        "tooltip": "Some CRO logic bespoke."
      },
      {
        "label": "Overlap",
        "value": "0.08",
        "level": "low",
        "tooltip": "Unique in the corpus."
      }
    ],
    "overlaps": []
  },
  "r5": {
    "id": "r5",
    "name": "Vendor_Payment_Reconciliation.xlsm",
    "owner": "CFO · Accounts Payable",
    "assignee": "K. Nair",
    "timing_cat": 2,
    "outcome": "box4",
    "disposition": "Document",
    "target_state_label": "Retain & improve",
    "confidence": 0.83,
    "rationale": "Low complexity + low criticality + low reg exposure. Keep as-is with owner attestation and a governance-doc render.",
    "purpose": "Reconciles Oracle AP disbursements against SAP Ariba vendor invoices. Small enough to retain with owner attestation.",
    "last_modified": "2 weeks ago",
    "usage_summary": "Monthly · 12 runs",
    "sources": [
      "Oracle AP",
      "SAP Ariba"
    ],
    "targets": [
      "AP Reconciliation Report"
    ],
    "transforms": [
      {
        "op": "JOIN",
        "desc": "ap ⨝ ariba on invoice_id"
      },
      {
        "op": "FILTER",
        "desc": "status = paid"
      }
    ],
    "transform_count": 5,
    "counts": {
      "formulas": 64,
      "macros": 3,
      "hardcodes": 1,
      "lookups": 2,
      "total_statements": 88,
      "distinct_rules": 1
    },
    "logic_rows": [
      {
        "tag": "HC",
        "location": "Sheet1!D8 · Tolerance",
        "code_html": "IF(ABS(diff) &lt; <b style=\"color:#ff6b8a\">50</b>, \"Match\", \"Investigate\")"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.24 · Low",
        "level": "low",
        "tooltip": "Small downstream footprint."
      },
      {
        "label": "Reg exposure",
        "value": "0.14",
        "level": "low",
        "tooltip": "Not regulated."
      },
      {
        "label": "Platform replace",
        "value": "0.44",
        "level": "medium",
        "tooltip": "Partly absorbable into SAP Ariba workflows."
      },
      {
        "label": "Overlap",
        "value": "0.06",
        "level": "low",
        "tooltip": "Unique."
      }
    ],
    "overlaps": []
  },
  "r6": {
    "id": "r6",
    "name": "Interest_Rate_Sensitivity_Model.xlsm",
    "owner": "CRO · IRRBB",
    "assignee": "D. Park",
    "timing_cat": 1,
    "outcome": "box1",
    "disposition": "Migrate",
    "target_state_label": "Full SDLC rebuild",
    "confidence": 0.89,
    "rationale": "Hand-written VBA, high complexity, high reg exposure. No enterprise asset absorbs this class of analytics. Full rebuild in Databricks.",
    "purpose": "Duration + convexity analytics across the banking book. Hand-written VBA — 1,247 lines. Feeds IRRBB reporting.",
    "last_modified": "6 hours ago",
    "usage_summary": "Weekly · 52 runs",
    "sources": [
      "Bloomberg",
      "Oracle GL",
      "Yield curve"
    ],
    "targets": [
      "IRRBB Sensitivity Report"
    ],
    "transforms": [
      {
        "op": "JOIN",
        "desc": "positions ⨝ yield_curve"
      },
      {
        "op": "FORMULA",
        "desc": "duration + convexity"
      },
      {
        "op": "AGGREGATE",
        "desc": "sensitivity BY bucket"
      }
    ],
    "transform_count": 22,
    "counts": {
      "formulas": 203,
      "macros": 31,
      "hardcodes": 8,
      "lookups": 5,
      "total_statements": 1247,
      "distinct_rules": 1
    },
    "logic_rows": [
      {
        "tag": "FN",
        "location": "Module5.bas:214 · Modified duration",
        "code_html": "ModDuration = MacDuration / (1 + ytm/freq)"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.79 · High",
        "level": "high",
        "tooltip": "IRRBB feeds regulatory reporting."
      },
      {
        "label": "Reg exposure",
        "value": "0.85",
        "level": "high",
        "tooltip": "IRRBB-tagged."
      },
      {
        "label": "Platform replace",
        "value": "0.19",
        "level": "high",
        "tooltip": "Bespoke analytics."
      },
      {
        "label": "Overlap",
        "value": "0.03",
        "level": "low",
        "tooltip": "Unique."
      }
    ],
    "overlaps": []
  },
  "r7": {
    "id": "r7",
    "name": "Counterparty_Exposure_ETL.dsx",
    "owner": "CRO · Counterparty",
    "assignee": "A. Osei",
    "timing_cat": 2,
    "outcome": "box2",
    "disposition": "Migrate",
    "target_state_label": "Governed / light SDLC",
    "confidence": 0.79,
    "rationale": "DataStage v11 EOL 2026 forces migration. Not platform-absorbable — light SDLC in Snowpark recommended.",
    "purpose": "DataStage ETL for counterparty exposure aggregation. Source Sybase; target Genius Bank.",
    "last_modified": "11 days ago",
    "usage_summary": "Daily · 28 runs",
    "sources": [
      "Sybase · Counterparty"
    ],
    "targets": [
      "Genius Bank · Exposure Register"
    ],
    "transforms": [
      {
        "op": "JOIN",
        "desc": "exposures ⨝ ratings"
      },
      {
        "op": "AGGREGATE",
        "desc": "PFE BY counterparty"
      }
    ],
    "transform_count": 9,
    "counts": {
      "formulas": 0,
      "macros": 0,
      "hardcodes": 2,
      "lookups": 4,
      "total_statements": 64,
      "distinct_rules": 1
    },
    "logic_rows": [
      {
        "tag": "FN",
        "location": "Stage_JoinRatings · Rating lookup",
        "code_html": "LOOKUP rating_master ON counterparty_id"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.55 · Medium",
        "level": "medium",
        "tooltip": "Medium downstream footprint."
      },
      {
        "label": "Reg exposure",
        "value": "0.42",
        "level": "medium",
        "tooltip": "Feeds capital calc."
      },
      {
        "label": "Platform replace",
        "value": "0.36",
        "level": "medium",
        "tooltip": "Some Snowpark absorption possible."
      },
      {
        "label": "Overlap",
        "value": "0.05",
        "level": "low",
        "tooltip": "Unique."
      }
    ],
    "overlaps": []
  },
  "r8": {
    "id": "r8",
    "name": "FX_Daily_Snapshot_v3_backup.xlsm",
    "owner": "Treasury · FX",
    "assignee": "unassigned",
    "timing_cat": 3,
    "outcome": "decommission",
    "disposition": "Decommission",
    "target_state_label": "Sunset",
    "confidence": 0.88,
    "rationale": "Superseded backup. Owner unassigned. Sunset with archive.",
    "purpose": "Backup copy of a superseded FX snapshot workflow.",
    "last_modified": "520 days ago",
    "usage_summary": "Idle",
    "sources": [
      "Bloomberg FX"
    ],
    "targets": [
      "(archived)"
    ],
    "transforms": [
      {
        "op": "AGGREGATE",
        "desc": "daily close"
      }
    ],
    "transform_count": 2,
    "counts": {
      "formulas": 42,
      "macros": 0,
      "hardcodes": 0,
      "lookups": 1,
      "total_statements": 52,
      "distinct_rules": 1
    },
    "logic_rows": [
      {
        "tag": "FN",
        "location": "Sheet1!A1",
        "code_html": "(archived backup)"
      }
    ],
    "signals": [
      {
        "label": "Blast radius",
        "value": "0.02",
        "level": "low",
        "tooltip": "No consumers."
      },
      {
        "label": "Reg exposure",
        "value": "0.00",
        "level": "low",
        "tooltip": "None."
      },
      {
        "label": "Platform replace",
        "value": "1.00",
        "level": "low",
        "tooltip": "Fully replaced."
      },
      {
        "label": "Overlap",
        "value": "0.98",
        "level": "high",
        "tooltip": "Duplicate of v3."
      }
    ],
    "overlaps": [
      {
        "name": "FX_Daily_Snapshot_v3.xlsm",
        "similarity": 0.98
      }
    ]
  }
};

export const frameworkSummary: FrameworkSummary = {
  "smbc_categories": [
    {
      "id": 1,
      "title": "Cat 1 — Lift & Shift",
      "count": 11,
      "subtitle": "Genius Bank / Manubank-linked"
    },
    {
      "id": 2,
      "title": "Cat 2 — Process Optimization",
      "count": 43,
      "subtitle": "Modernization-linked; Lakehouse path"
    },
    {
      "id": 3,
      "title": "Cat 3 — Strategic Planning",
      "count": 100,
      "subtitle": "Unresolved; full disposition required"
    }
  ],
  "boxes": [
    {
      "id": 1,
      "title": "Box 1 — Full SDLC rebuild",
      "risk": "HIGH",
      "count": 34,
      "risk_desc": "HIGH risk — retire and rebuild",
      "from_cats": [
        {
          "cat": 2,
          "level": "fit"
        },
        {
          "cat": 3,
          "level": "fit"
        }
      ]
    },
    {
      "id": 3,
      "title": "Box 3 — Platform migration",
      "risk": "HIGH",
      "count": 39,
      "risk_desc": "HIGH risk — absorbed into enterprise asset",
      "from_cats": [
        {
          "cat": 1,
          "level": "fit"
        },
        {
          "cat": 3,
          "level": "fit"
        }
      ]
    },
    {
      "id": 2,
      "title": "Box 2 — Governed / light SDLC",
      "risk": "MEDIUM",
      "count": 41,
      "risk_desc": "MEDIUM risk — user-owned, platform-controlled",
      "from_cats": [
        {
          "cat": 2,
          "level": "fit"
        },
        {
          "cat": 3,
          "level": "fit"
        }
      ]
    },
    {
      "id": 4,
      "title": "Box 4 — Retain & improve",
      "risk": "LOW",
      "count": 14,
      "risk_desc": "LOW risk — EUC retained with controls",
      "from_cats": [
        {
          "cat": 3,
          "level": "fit"
        }
      ]
    }
  ]
};

export const phases: PhaseDef[] = [
  {
    "id": 1,
    "title": "Governance & Program Setup",
    "statusLine": "Complete · Week 1",
    "progress": "complete",
    "artifacts": [
      {
        "id": "p1_a1",
        "phase": 1,
        "name": "EUC Retirement Path Decision Framework",
        "format": "PDF",
        "size_label": "2.4 MB",
        "state": "Ready",
        "updated_label": "Wk 1"
      },
      {
        "id": "p1_a2",
        "phase": 1,
        "name": "GCC Team Onboarding Playbook",
        "format": "DOCX",
        "size_label": "1.1 MB",
        "state": "Ready",
        "updated_label": "Wk 1"
      }
    ]
  },
  {
    "id": 2,
    "title": "Population Intelligence & Sequencing",
    "statusLine": "In progress · Week 2–3",
    "progress": "active",
    "artifacts": [
      {
        "id": "p2_a1",
        "phase": 2,
        "name": "Classification Triage — 154 EUCs",
        "format": "XLSX",
        "size_label": "3.8 MB",
        "state": "Ready",
        "updated_label": "Wk 2"
      },
      {
        "id": "p2_a2",
        "phase": 2,
        "name": "CxO Owner Engagement Reports (CFO / CRO / COO)",
        "format": "PDF",
        "size_label": "6.2 MB",
        "state": "Ready",
        "updated_label": "Wk 2"
      },
      {
        "id": "p2_a3",
        "phase": 2,
        "name": "Data Dependency Map vs. Lakehouse Assets",
        "format": "JSON",
        "size_label": "890 KB",
        "state": "Ready",
        "updated_label": "Wk 3"
      },
      {
        "id": "p2_a4",
        "phase": 2,
        "name": "Human-Review Flags — Ambiguous EUCs",
        "format": "CSV",
        "size_label": "240 KB",
        "state": "Draft",
        "updated_label": "Wk 3"
      }
    ]
  },
  {
    "id": 3,
    "title": "Retirement Execution",
    "statusLine": "Queued · Week 4–8",
    "progress": "queued",
    "artifacts": [
      {
        "id": "p3_a1",
        "phase": 3,
        "name": "Per-EUC Migration Specs (128 artifacts)",
        "format": "MD",
        "size_label": "—",
        "state": "Queued",
        "updated_label": "Wk 4+"
      },
      {
        "id": "p3_a2",
        "phase": 3,
        "name": "Power BI Data Model Scaffolds",
        "format": "PBIX",
        "size_label": "—",
        "state": "Queued",
        "updated_label": "Wk 5+"
      },
      {
        "id": "p3_a3",
        "phase": 3,
        "name": "Regression Test Frameworks + Audit Trails",
        "format": "ZIP",
        "size_label": "—",
        "state": "Queued",
        "updated_label": "Wk 6+"
      },
      {
        "id": "p3_a4",
        "phase": 3,
        "name": "RAG Monitoring & Escalation Drafts",
        "format": "PDF",
        "size_label": "—",
        "state": "Queued",
        "updated_label": "Wk 7+"
      }
    ]
  },
  {
    "id": 4,
    "title": "Sustain, Transfer & Prevent Recurrence",
    "statusLine": "Planned · Week 9+",
    "progress": "queued",
    "artifacts": [
      {
        "id": "p4_a1",
        "phase": 4,
        "name": "Population Drift Detection Report",
        "format": "PDF",
        "size_label": "—",
        "state": "Planned",
        "updated_label": "Wk 9+"
      },
      {
        "id": "p4_a2",
        "phase": 4,
        "name": "Health Dashboard Content (Regulator Review)",
        "format": "PBIX",
        "size_label": "—",
        "state": "Planned",
        "updated_label": "Wk 9+"
      },
      {
        "id": "p4_a3",
        "phase": 4,
        "name": "Stakeholder & Regulator Comms Drafts",
        "format": "DOCX",
        "size_label": "—",
        "state": "Planned",
        "updated_label": "Wk 10+"
      },
      {
        "id": "p4_a4",
        "phase": 4,
        "name": "Jarvis Configuration Transfer Package",
        "format": "ZIP",
        "size_label": "—",
        "state": "Planned",
        "updated_label": "Wk 12"
      }
    ]
  }
];
